/*
 * Copyright © HatioLab Inc. All rights reserved.
 */

import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'

import { RealObject } from '@hatiolab/things-scene'
import { Stock } from './stock.js'
import { RackTable } from './rack-table.js'
import { Visualizer } from './visualizer.js'

export class Rack extends RealObject {
  private _frame?: THREE.BufferGeometry
  private _board?: THREE.BufferGeometry

  static rackFrameGeometry = new THREE.BoxGeometry(1, 1, 1)
  static boardGeometry = new THREE.PlaneGeometry(1, 1, 1, 1)

  private static _boardMaterial?: THREE.MeshStandardMaterial

  get visualizer(): Visualizer | undefined {
    var component = this.component

    while (component) {
      if (component.state.type == 'visualizer') {
        return component as unknown as Visualizer
      }
      component = component.parent
    }
  }

  /**
   * Stock 등록 대상: 1순위 조상 Visualizer(하위호환), 2순위 서비스 레지스트리의 stock-hub
   */
  get stockRegistry(): { putObject(id: string, obj: RealObject): void } | undefined {
    const vis = this.visualizer
    if (vis) return vis
    return (this.component.root as any)?.getService?.('stock')
  }

  get rackTable(): RackTable | undefined {
    var component = this.component

    while (component) {
      if (component.state.type == 'rack-table') {
        return component as unknown as RackTable
      }
      component = component.parent
    }
  }

  get cz() {
    var { shelves = 1, depth = 1 } = this.rackTable!.state

    return 0.5 * depth * shelves
  }

  static get boardMaterial() {
    if (!Rack._boardMaterial) {
      Rack._boardMaterial = new THREE.MeshStandardMaterial({
        color: '#dedede',
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -0.1
      })
    }

    return Rack._boardMaterial
  }

  get frame() {
    return this._frame
  }

  get board() {
    return this._board
  }

  build() {
    super.build()

    var { depth, hideRackFrame, shelves, shelfLocations: commonShelfLocation, stockScale = 0.7 } = this.rackTable!.state

    var { width, height, shelfLocations = commonShelfLocation, binLocations = '' } = this.component.state

    let scale = stockScale

    var shelfLocIds

    if (!shelfLocations) {
      shelfLocIds = []
      for (var i = 0; i < shelves; i++) shelfLocIds.push(i + 1)
    } else shelfLocIds = shelfLocations.split(/\s*,\s*/)

    var shelfBins = binLocations.trim().split('\n').reverse()

    if (!hideRackFrame) {
      this._frame = this.createRackFrame(width, height, depth * shelves)
      this._board = this.createRackBoards(shelves, width, height, depth, shelfLocIds)
    }

    for (var i = 0; i < shelves; i++) {
      let bottom = -depth * shelves * 0.5
      if (shelfLocIds[i] == '') {
        continue
      }

      var bins = (shelfBins[i] || '').trim().split(/\s*,\s*/)

      var binWidth = width / (bins.length || 1)
      for (var b = 0; b < bins.length; b++) {
        let stock = new Stock(this.component, {
          width: binWidth * scale,
          height: height * scale,
          depth: depth * scale
        })
        stock.update()

        let stockDepth = depth * scale

        stock.object3d.position.set(
          (width / 2) * ((2 * b - (bins.length - 1)) / bins.length),
          bottom + depth * i + stockDepth * 0.5,
          0
        )
        var binCode = (bins[b] || '').replace('.', '')
        stock.object3d.name = `${this.makeLocationString(shelfLocIds[i])}${binCode}`

        this.object3d.add(stock.object3d)
        this.stockRegistry?.putObject(stock.object3d.name, stock)
      }
    }
  }

  createRackFrame(w: number, h: number, d: number): THREE.BufferGeometry {
    const frameWeight = Math.round(Math.min(w, h) / 10)
    const geometries = []

    for (let i = 0; i < 4; i++) {
      const geometry = Rack.rackFrameGeometry.clone()
      geometry.scale(frameWeight, d, frameWeight)

      switch (i) {
        case 0:
          geometry.translate(w / 2, 0, h / 2)
          break
        case 1:
          geometry.translate(w / 2, 0, -h / 2)
          break
        case 2:
          geometry.translate(-w / 2, 0, h / 2)
          break
        case 3:
          geometry.translate(-w / 2, 0, -h / 2)
          break
      }

      geometries.push(geometry)
    }

    return BufferGeometryUtils.mergeGeometries(geometries)
  }

  createRackBoards(
    shelves: number,
    width: number,
    height: number,
    depth: number,
    shelfLocIds: string[]
  ): THREE.BufferGeometry {
    let bottom = -depth * shelves * 0.5
    const geometries = []

    for (let i = 1; i < shelves; i++) {
      if (shelfLocIds[i] === '') {
        continue
      }

      const geometry = Rack.boardGeometry.clone()

      geometry.scale(width, height, 1)
      geometry.rotateX(Math.PI / 2)
      geometry.translate(0, bottom + depth * i, 0)

      geometries.push(geometry)
    }

    return BufferGeometryUtils.mergeGeometries(geometries)
  }

  makeLocationString(shelfString: string) {
    var { section = '', unit = '' } = this.component.state
    var { locPattern = '{z}{s}-{u}-{sh}', zone = '' } = this.rackTable!.state

    var locationString = locPattern

    locationString = locationString.replace(/{z}/i, zone)
    locationString = locationString.replace(/{s}/i, section)
    locationString = locationString.replace(/{u}/i, unit)
    locationString = locationString.replace(/{sh}/i, shelfString)

    return locationString
  }

  makeShelfString(pattern: string, shelf: number, length: number) {
    /**
     *  pattern #: 숫자
     *  pattern 0: 고정 자리수
     *  pattern -: 역순
     */

    if (!pattern || !shelf || !length) return

    var isReverse = /^\-/.test(pattern)
    pattern = pattern.replace(/#+/, '#')

    var fixedLength = (pattern.match(/0/g) || []).length || 0
    var shelfString = String(isReverse ? length - shelf + 1 : shelf)

    if (shelfString.length > fixedLength && fixedLength > 0) {
      shelfString = shelfString.substring(shelfString.length - fixedLength)
    } else {
      var prefix = ''
      for (var i = 0; i < fixedLength - shelfString.length; i++) {
        prefix += '0'
      }
      shelfString = prefix + shelfString
    }

    return shelfString
  }

  updateAlpha() {}
}
